"use client";

// app/HomeClient.tsx
// 首页（仪表盘）—— 行动指挥中心
//
// 设计（乔布斯视角）：
//   原首页 12 个区块单列，CurrentTaskCard 被推到第 5 位，3 秒看不到答案。
//   重构为 6 区结构（需求 4：学习队列移到最下面，KPI 卡片作为快速入口）：
//     1. Hero 行动区：CurrentTaskCard + 番茄钟入口 + 低能量休息链接
//     2. KPI 三宫格：今日学习清单 N 项（可点击进入学习）/ 已完成 X 项 / 连续打卡 N 天
//     3. AI 教练洞察区：HomeInsightsCard（成就 + 健康提醒）
//        2026-07-29：移除能力画像和AI质量卡片到 /stats 仪表盘（减少首屏负载）
//     4. 能量趋势迷你图（新账户无数据时隐藏）
//     5. 7 天热力图（常驻，新账户无打卡记录时隐藏）
//     6. 今日学习队列（移到最下面作为详细视图，KPI 卡片已能快速进入学习）
//
// 第 5 轮简化：
//   - 折叠区里的「今日情绪」「错题本」与 EmotionQuickPicker / /mistakes 重复 → 删除
//   - 「更多」折叠按钮无存在意义 → 移除折叠逻辑，7 天热力图常驻
//
// 新账户隐藏空数据区块（需求 4）：
//   - 第 3 区（AI 教练洞察）：仅在 newAchievements.length || healthAlerts.length 时渲染
//     2026-07-29：移除能力画像和AI质量摘要（移到 /stats 仪表盘 tab）
//   - 第 4 区能量趋势迷你图：仅在 energyTrend 有非 null 值时渲染
//   - 第 5 区 7 天热力图：仅在 heatmapData 有非 0 分钟数据时渲染
//   - KPI 三宫格第 2 格「已完成」：todayCompletedCount > 0 时才着色突出
//
// 新增：
//   - 用户画像摘要（beginner/intermediate/advanced 节点数 + 偏好时段）
//   - 能量趋势迷你图
//   - AI 质量摘要（今日调用数 + 采纳率）
//   - 第 2 阶段：studyQueue 智能排序学习队列（合并 learn + review 单一待办流）
//   - 需求 4：KPI 第 1 格可点击进入学习（队列第一项或 /learn/new 兜底）

import { useState, useCallback } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useHomeData, getStreakMeta } from "@/lib/home";
import { buildSceneUrl } from "@/lib/study-queue/nav-params";
import { CurrentTaskCard } from "@/components/CurrentTaskCard";
import { PathProgressBar } from "@/components/PathProgressBar";
import { PathCoachInsight } from "@/components/PathCoachInsight";
import { EmotionQuickPicker } from "@/components/EmotionQuickPicker";
import { Icon, type IconName } from "@/components/Icon";
import { Button, LinkButton } from "@/components/ui";
import { HomeInsightsCard } from "@/components/HomeInsightsCard";
import { EnergyTrendMini } from "@/components/EnergyTrendMini";
import { POMODORO_OPEN_EVENT } from "@/lib/timer/pomodoro";
import type { PublicProfile } from "@/lib/types";
import { getLastSyncedAt, uploadIncremental } from "@/lib/sync";
import { confirmDialog } from "@/lib/confirm-dialog";

// 2026-07-30 性能优化：UsernameSetupModal 改动态加载
// 仅在用户点击分享且未设置用户名时才需要，避免首屏加载弹窗组件 chunk。
const UsernameSetupModal = dynamic(
  () => import("@/components/UsernameSetupModal").then((m) => m.UsernameSetupModal),
  { ssr: false, loading: () => null },
);

// 首屏骨架屏：与 HomeClient 布局对齐，提供"结构感"而非白屏
// 2026-07-27：从 app/page.tsx 移到此处 export，让 Suspense fallback 和
// useHomeData isLoading 共用同一骨架屏，避免两段式跳变（chunk 加载骨架屏 →
// 数据加载假数据 → 真实数据）
export function HomeSkeleton() {
  // 2026-07-29 重构：骨架屏与 HomeClient 真实 6 区结构严格对齐，
  // 每个区块用固定 min-h 防止 hydration 后高度跳变（CLS 归零）。
  // 旧骨架屏有"错题区""快捷入口"等已移除区块，与真实内容不匹配导致 CLS。
  return (
    <div className="min-h-screen p-4 max-w-2xl mx-auto pb-20 dark:bg-gray-900 animate-pulse">
      {/* 1. Hero 行动区：问候+分享 → 路径进度卡片 → 番茄钟按钮 */}
      <section className="mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-7 w-20 bg-gray-100 dark:bg-gray-700 rounded-full" />
        </div>
        {/* PathProgressBar 占位（固定 h-28 匹配真实卡片高度） */}
        <div className="min-h-[112px] bg-gray-100 dark:bg-gray-800 rounded-2xl mb-2" />
        {/* PathCoachInsight 占位 */}
        <div className="min-h-[40px] bg-gray-100 dark:bg-gray-800 rounded-lg mb-2" />
        {/* 番茄钟按钮占位 */}
        <div className="min-h-[52px] bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </section>

      {/* 1.5 情绪快捷选择占位 */}
      <div className="min-h-[72px] mb-5 bg-gray-100 dark:bg-gray-800 rounded-xl" />

      {/* 2. KPI 三宫格占位（固定 h-24 匹配真实卡片高度） */}
      <section className="mb-5 grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="min-h-[96px] bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl" />
        ))}
      </section>

      {/* 3. AI 教练洞察区占位（条件渲染，骨架屏保留固定高度占位防 CLS） */}
      <section className="mb-5">
        <div className="min-h-[80px] bg-gray-100 dark:bg-gray-800 rounded-lg" />
      </section>

      {/* 4. 能量趋势迷你图占位（条件渲染） */}
      <section className="mb-5">
        <div className="min-h-[60px] bg-gray-100 dark:bg-gray-800 rounded-lg" />
      </section>

      {/* 5. 7 天热力图占位 */}
      <section className="mb-5">
        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex-1 min-h-[48px] bg-gray-100 dark:bg-gray-800 rounded" />
          ))}
        </div>
      </section>

      {/* 6. 今日学习队列占位 */}
      <section>
        <div className="h-5 w-28 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="min-h-[56px] bg-gray-100 dark:bg-gray-800 rounded-lg" />
          ))}
        </div>
      </section>
    </div>
  );
}

export default function HomeClient() {
  const {
    streak,
    lastStreak,
    heatmapData,
    todayEnergy,
    hasPlans,
    username,
    newAchievements,
    healthAlerts,
    energyTrend,
    studyQueue,
    todayCompletedCount,
    careerPath,
    coachInsight,
    reload,
    isLoading,
  } = useHomeData();

  const [shareMsg, setShareMsg] = useState<string>("");
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  // 用户名设置成功后，是否继续走分享流程（避免用户设置完后还要再点一次分享）
  const [pendingShare, setPendingShare] = useState(false);

  const doShare = useCallback((uname: string) => {
    const shareUrl = `${window.location.origin}/u/${encodeURIComponent(uname)}`;
    const shareText = "来看看我的开发者成长主页";
    if (navigator.share) {
      void navigator.share({ title: "devpath", text: shareText, url: shareUrl }).catch(() => {
        // 用户取消分享，静默
      });
    } else {
      navigator.clipboard
        ?.writeText(shareUrl)
        .then(() => {
          setShareMsg("链接已复制到剪贴板");
          setTimeout(() => setShareMsg(""), 2500);
        })
        .catch(() => {
          setShareMsg(shareUrl);
          setTimeout(() => setShareMsg(""), 5000);
        });
    }
  }, []);

  async function handleShare() {
    if (!username) {
      // 未设置用户名：弹窗引导设置，设置成功后自动继续分享
      setPendingShare(true);
      setUsernameModalOpen(true);
      return;
    }
    // 检测是否曾同步到远程——未同步则分享链接打开会 404，需提示用户先同步
    try {
      const lastSync = await getLastSyncedAt();
      if (!lastSync) {
        const ok = await confirmDialog({
          title: "同步到云端？",
          message:
            "你的主页尚未同步到云端，朋友打开分享链接会看不到内容。是否现在同步？",
          confirmText: "同步并分享",
          cancelText: "仅本地分享",
        });
        if (ok) {
          setShareMsg("正在同步到云端…");
          try {
            await uploadIncremental();
          } catch {
            // 同步失败不阻断分享，仅提示
          }
          setShareMsg("");
        }
      }
    } catch {
      // 同步状态读取失败，降级为直接分享
    }
    doShare(username);
  }

  function handleUsernameSaved(profile: PublicProfile) {
    // 设置成功后，刷新首页数据（让 useHomeData 重新读到新 username）
    reload();
    if (pendingShare && profile.username) {
      setPendingShare(false);
      doShare(profile.username);
    }
  }

  const heatColor = (minutes: number) => {
    if (minutes === 0) return "bg-gray-100 dark:bg-gray-700";
    if (minutes < 15) return "bg-green-200 dark:bg-green-900/40";
    if (minutes < 30) return "bg-green-400 dark:bg-green-700";
    if (minutes < 60) return "bg-green-500 dark:bg-green-600";
    return "bg-green-700 dark:bg-green-500";
  };

  const streakMeta = getStreakMeta(streak, lastStreak);
  const lowEnergy = todayEnergy !== null && todayEnergy <= 2;
  // 需求 4：7 天热力图仅在至少 1 天有打卡数据时才显示（新账户隐藏）
  const heatmapHasData = heatmapData.some((d) => d.minutes > 0);

  // ============ 加载态：首屏骨架屏 ============
  // 2026-07-27：useHomeData 首次加载时显示骨架屏，避免 0/null 假数据跳变
  // reload（visibilitychange/focus）不重新设 isLoading=true，数据直接更新不闪骨架屏
  if (isLoading) {
    return <HomeSkeleton />;
  }

  // ============ 新用户引导 ============
  if (hasPlans === false) {
    return (
      <div className="min-h-screen p-4 max-w-2xl mx-auto pb-20 dark:bg-gray-900 flex flex-col items-center justify-center text-center">
        <div className="mb-6">
          <Icon name="sparkles" className="w-16 h-16 mx-auto text-purple-500" />
        </div>
        <h1 className="text-2xl font-bold mb-3">欢迎来到 devpath</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md">
          AI 驱动的开发者成长 OS。告诉 AI 你想学什么，它帮你拆知识树、排计划、出面试题、按遗忘曲线复习。
        </p>
        <LinkButton
          href="/learn/new"
          size="lg"
          className="rounded-full px-8 shadow-lg"
        >
          开始第一个学习计划 →
        </LinkButton>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 max-w-2xl mx-auto pb-20 dark:bg-gray-900">
      {/* ============ 1. Hero 行动区 ============ */}
      <section className="mb-5">
        {/* 顶部：问候 + 分享 */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold">今天</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {new Date().toLocaleDateString("zh-CN", { month: "long", day: "numeric", weekday: "long" })}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon="share"
            onClick={handleShare}
          >
            分享
          </Button>
        </div>
        {shareMsg && (
          <div
            role="status"
            aria-live="polite"
            className="mb-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800 p-2 text-sm text-blue-700 dark:text-blue-300 break-all"
          >
            {shareMsg}
          </div>
        )}

        {/* V2: Path 路径进度（如果有职业路径，作为视觉焦点替代 CurrentTaskCard）
            设计（乔布斯视角）：用户打开 app 第一眼看到"离 offer 还有多远"
              - 有 careerPath：PathProgressBar + PathCoachInsight
              - 无 careerPath（旧用户）：显示"选择职业路径"引导卡片 + CurrentTaskCard */}
        {careerPath ? (
          <>
            <PathProgressBar
              careerTitle={careerPath.title}
              icon={careerPath.icon}
              progress={careerPath.progress}
              weeksLeft={careerPath.weeksLeft}
              currentNodeTitle={careerPath.currentNodeTitle}
            />
            <div className="mt-2">
              <PathCoachInsight insight={coachInsight} />
            </div>
            {/* 重新选择岗位入口：用户选错路径时可切换
                语义：旧 plan 保留在 /learn/list，新 plan 立即开始训练，
                my:onboarding 记录会被新选择覆盖 */}
            <div className="mt-2 text-center">
              <Link
                href="/onboarding"
                className="text-xs text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
              >
                选错方向了？重新选择岗位 →
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* 旧用户引导：选择职业路径，获得专属学习路径图 */}
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="map" className="w-5 h-5" />
                <h2 className="text-base font-bold">选择你的 AI 职业路径</h2>
              </div>
              <p className="text-sm opacity-90 mb-3">
                选一个方向，获得专属学习路径图——从入门到拿 offer，全程陪练。
              </p>
              <LinkButton
                href="/onboarding"
                variant="secondary"
                size="sm"
                className="bg-white dark:bg-gray-50 text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-100 font-bold"
                leftIcon="zap"
              >
                选择职业路径 →
              </LinkButton>
            </div>
            <CurrentTaskCard />
          </>
        )}

        {/* 行动入口：番茄钟（常驻）+ 低能量休息提示（条件，文字链接避免视觉过重）
            入口改造：原 <Link href="/timer"> 跳转路由 → 移除 /timer 路由
            改为派发 POMODORO_OPEN_EVENT 事件，全局 PomodoroWidget 监听后打开 card 浮窗
            （路由 → 浮窗工具：更符合 AI Native 风格，不离开当前页就能开始专注） */}
        <Button
          variant="ghost"
          onClick={() => {
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent(POMODORO_OPEN_EVENT));
            }
          }}
          className="mt-2 w-full flex items-center justify-between rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-3 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors h-auto"
        >
          <span className="flex items-center gap-2 text-sm text-red-800 dark:text-red-300">
            <Icon name="tomato" className="w-4 h-4 text-red-500" />
            番茄钟 · 开始一段专注
          </span>
          <span className="text-xs text-red-700 dark:text-red-400 flex items-center gap-0.5">
            25:00 <Icon name="chevron-right" className="w-3.5 h-3.5" />
          </span>
        </Button>
        {lowEnergy && (
          <Link
            href="/rest"
            className="mt-1.5 block text-center text-xs text-green-700 dark:text-green-400 hover:underline"
          >
            <Icon name="leaf" className="w-3 h-3 inline mr-0.5" />
            检测到今天能量偏低，去休息一下？
          </Link>
        )}
      </section>

      {/* ============ 1.5 情绪快捷选择 ============ */}
      {/* 极简 3 emoji 一行（兴奋 / 平静 / 疲惫），点击即记录，无需展开折叠区。
          情绪觉察是核心差异化功能，从折叠区提到 Hero 区下方，3 秒内可触发自我觉察。
          深度记录（原因+影响+AI 建议）仍走折叠区 EmotionRecorder 或 /emotion 页。 */}
      <EmotionQuickPicker onRecorded={reload} />

      {/* ============ 2. KPI 三宫格（第 2 阶段：学习+复习合并为单一队列）
          需求 4：第一格「今日学习清单」可点击进入学习（队列第一项或 /learn/new 兜底）
          2026-07-23 优化：第 1 格视觉强化——蓝色渐变背景 + 右上角箭头 + "进入学习"提示，
          与第 2/3 格（纯展示）形成视觉区分，让"可点击"更明显
          2026-07-25 用户需求：学习计划都完成后（队列空），点击应进入「我的学习列表」
          而不是直接跳新建计划——让用户能选已有知识库做全量学习。
          规则：队列非空 → 第一项；队列空 + hasPlans → /learn/list；队列空 + !hasPlans → /learn/new ============ */}
      <section className="mb-5 grid grid-cols-3 gap-3">
        <Link
          href={
            studyQueue[0]
              ? (studyQueue[0].type === "review"
                  ? buildSceneUrl("/review", studyQueue[0], "home")
                  : buildSceneUrl(`/learn/${studyQueue[0].planId ?? ""}`, studyQueue[0], "home"))
              : (hasPlans ? "/learn/list" : "/learn/new")
          }
          className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/40 dark:to-gray-800 border border-blue-200 dark:border-blue-800 rounded-2xl p-4 text-center hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md hover:-translate-y-0.5 transition-all group relative"
        >
          {/* 右上角箭头图标：绝对定位，hover 时向右移动 */}
          <Icon
            name="chevron-right"
            className="absolute top-2 right-2 w-4 h-4 text-blue-400 dark:text-blue-500 group-hover:translate-x-0.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all"
          />
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{studyQueue.length}</p>
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 font-medium">今日学习清单</p>
          <p className="text-2xs text-blue-600 dark:text-blue-400 mt-0.5">
            {studyQueue.length > 0 ? "点击进入学习" : (hasPlans ? "查看学习列表" : "创建学习计划")}
          </p>
        </Link>
        <div
          aria-label={`今日已完成 ${todayCompletedCount} 项`}
          className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-4 text-center"
        >
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{todayCompletedCount}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">已完成</p>
        </div>
        <div
          aria-label={streak === 0 ? "今日去打卡" : `连续打卡 ${streak} 天`}
          className={`border rounded-2xl p-4 text-center ${streakMeta.color}`}
        >
          <p className="text-3xl font-bold flex items-center justify-center gap-1">
            {streakMeta.emoji ? (
              <Icon name={streakMeta.emoji as IconName} className="w-5 h-5 inline-block" />
            ) : null}
            {streak}
          </p>
          <p className="text-xs mt-1">{streak === 0 ? "去打卡" : streakMeta.sub}</p>
        </div>
      </section>

      {/* ============ 3. AI 教练洞察区（需求 4：新账户无数据时整区隐藏）============ */}
      {/* 2026-07-29 性能优化：移除能力画像和AI质量卡片（移到 /stats 仪表盘 tab） */}
      {(newAchievements.length > 0 || healthAlerts.length > 0) && (
        <section className="mb-5">
          <HomeInsightsCard
            newAchievements={newAchievements}
            healthAlerts={healthAlerts}
          />
        </section>
      )}

      {/* ============ 4. 能量趋势迷你图（需求 4：新账户无能量数据时隐藏）============ */}
      {energyTrend.some((v) => v !== null) && (
        <section className="mb-5">
          <EnergyTrendMini trend={energyTrend} todayEnergy={todayEnergy} />
        </section>
      )}

      {/* ============ 5. 7 天热力图（需求 1：移除折叠按钮+今日情绪+错题本，常驻显示；
                                       需求 4：新账户无打卡记录时隐藏）============ */}
      {heatmapHasData && (
        <section className="mb-5">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
            <Icon name="calendar" className="w-4 h-4" />
            最近 7 天
          </h3>
          <div className="flex gap-1">
            {heatmapData.map((d) => (
              <div key={d.date} className="flex-1 text-center">
                <div
                  className={`h-12 rounded ${heatColor(d.minutes)} flex items-end justify-center pb-1`}
                >
                  {d.minutes > 0 && (
                    <span className="text-xs text-white font-medium">{d.minutes}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{d.date.slice(5)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ============ 6. 今日学习队列（需求 4：移到最下面，作为详细视图）
          第 2 阶段：studyQueue 渲染——合并待学+待复习，按 priority 排序
          需求 3：header 右侧新增「+ 新建计划」入口，跳 /learn/new
          2026-07-25 用户需求：当学习列表有数据时（hasPlans），header 入口变为
          「我的学习列表」→ /learn/list；无数据时保持「+ 新建计划」→ /learn/new。
          这样用户在做今日队列时也能一键进入全量知识库列表 ============ */}
      <section className="mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <Icon name="calendar-check" className="w-4 h-4" />
            今日学习队列
          </h2>
          {hasPlans ? (
            <Link
              href="/learn/list"
              aria-label="我的学习列表"
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-0.5"
            >
              <Icon name="list" className="w-3.5 h-3.5" />
              我的学习列表
            </Link>
          ) : (
            <Link
              href="/learn/new"
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-0.5"
            >
              <Icon name="plus" className="w-3.5 h-3.5" />
              新建计划
            </Link>
          )}
        </div>

        {studyQueue.length > 0 ? (
          <div className="space-y-1.5">
            {studyQueue.slice(0, 5).map((task) => {
              const href = task.type === "review"
                ? buildSceneUrl("/review", task, "home")
                : buildSceneUrl(`/learn/${task.planId ?? ""}`, task, "home");
              return (
                <Link
                  key={task.id}
                  href={href}
                  className="flex items-start gap-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-2.5 hover:shadow-md transition-shadow"
                >
                  <span
                    className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                      task.type === "review"
                        ? "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
                        : "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                    }`}
                  >
                    {task.type === "review" ? "复" : "学"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate text-gray-800 dark:text-gray-200">{task.title}</p>
                    {task.reason && (
                      <p className="text-2xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {task.reason}
                      </p>
                    )}
                  </div>
                  {task.estimatedMinutes && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{task.estimatedMinutes}min</span>
                  )}
                  <Icon name="chevron-right" className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 shrink-0 mt-1" />
                </Link>
              );
            })}
            {studyQueue.length > 5 && (
              <Link
                href="/review"
                className="block text-center text-xs text-blue-600 hover:underline dark:text-blue-400 pt-1"
              >
                查看全部 {studyQueue.length} 项 →
              </Link>
            )}
          </div>
        ) : (
          /* 空队列 EmptyState（2026-07-23 优化）：
             - todayCompletedCount > 0：今日清单已清空（成就感）
             - todayCompletedCount === 0：今天没有待学习项（引导创建）
             CTA 用 LinkButton 强化视觉，引导用户去 /learn/new 创建计划 */
          <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6 text-center">
            <Icon
              name={todayCompletedCount > 0 ? "check-circle" : "sparkles"}
              className={`w-10 h-10 mx-auto mb-2 ${todayCompletedCount > 0 ? "text-green-500" : "text-blue-400 dark:text-blue-500"}`}
            />
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
              {todayCompletedCount > 0 ? "今日清单已清空，干得漂亮！" : "今天没有待学习项"}
            </p>
            <p className="text-2xs text-gray-500 dark:text-gray-400 mb-3">
              {todayCompletedCount > 0 ? "休息一下，或规划明天的学习" : "创建一个学习计划，开始你的成长之旅"}
            </p>
            <LinkButton
              href="/learn/new"
              size="sm"
              variant="primary"
              leftIcon="plus"
            >
              {todayCompletedCount > 0 ? "新建计划" : "创建学习计划"}
            </LinkButton>
          </div>
        )}
      </section>

      {/* 用户名设置弹窗：未设置用户名时点击分享触发 */}
      <UsernameSetupModal
        open={usernameModalOpen}
        onClose={() => {
          setUsernameModalOpen(false);
          setPendingShare(false);
        }}
        onSaved={handleUsernameSaved}
      />
    </div>
  );
}
